'use client';

import { c as _c } from "react/compiler-runtime";
import { type DirectPurchaseCheckout, directPurchaseFiat, type FundingOperation, formatFiatUnits, type OnrampClient, onrampManifest } from '@morfi/onramp-sdk';
import { useFundingAmountDraft, useFundingCurrency, useFundingInlineCheckout } from '@morfi/onramp-sdk/react';
import type { OnrampRealtime } from '@morfi/onramp-sdk/realtime';
import { createMoonPayWebProvider, type MoonPayWebProvider, webFundingMethods } from '@morfi/onramp-sdk/web';
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useEffectEvent } from 'react';
import { TOAST_IDS } from '@/config/toast-ids';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { useExchangeRates } from '@/providers/exchange-rate-provider';
import { useCurrencyStore } from '@/stores/currency';
import { useFundingStage } from '../shared/funding-chrome';
import { fundingLimitStorage } from '../shared/funding-limit-storage';
import { useFundingPageActive } from '../shared/use-funding-page-active';
import { useFundingResource } from '../shared/use-funding-resource';
import { checkoutOwnsChrome } from './funding-checkout-chrome';
import { FundingCheckoutView } from './funding-checkout-view';
import { FundingMethodPicker } from './funding-method-picker';
export interface Props {
  directPurchase?: DirectPurchaseCheckout;
  hostActive?: boolean;
  checkoutRevision?: number;
  open?: boolean;
  render?(content: ReactNode): ReactNode;
  realtime?: OnrampRealtime;
  client: OnrampClient;
  sessionToken: string;
  sessionProofId: string;
  authenticationRequested?: boolean;
  authenticationBusy?: boolean;
  authenticationError?: boolean;
  onAcceptAuthenticationTerms?(version: string): Promise<void>;
  onSessionRefresh(reason?: 'disconnect'): void;
  onClose(): void;
  onSignIn(): void;
  onExchange?: (() => void) | undefined;
}
export function FundingCheckout(props) {
  const $ = _c(5);
  let t0;
  if ($[0] !== props.sessionToken) {
    t0 = () => createMoonPayWebProvider({
      sessionToken: props.sessionToken,
      theme: {
        appearance: "dark"
      }
    });
    $[0] = props.sessionToken;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const provider = useFundingResource(t0);
  let t1;
  if ($[2] !== props || $[3] !== provider) {
    t1 = provider ? <CheckoutBody key={props.checkoutRevision} {...props} provider={provider} /> : props.render ? props.render(<FundingMethodPicker client={props.client} methods={[]} onExchange={props.onExchange} loading={true} />) : <FundingMethodPicker client={props.client} methods={[]} onExchange={props.onExchange} loading={true} />;
    $[2] = props;
    $[3] = provider;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function CheckoutBody(t0) {
  const $ = _c(84);
  const {
    client,
    realtime,
    sessionProofId,
    directPurchase,
    authenticationRequested: t1,
    authenticationBusy: t2,
    authenticationError: t3,
    onAcceptAuthenticationTerms,
    provider,
    onSessionRefresh,
    onSignIn,
    onExchange,
    render,
    open: t4,
    onClose,
    hostActive: t5
  } = t0;
  const authenticationRequested = t1 === undefined ? false : t1;
  const authenticationBusy = t2 === undefined ? false : t2;
  const authenticationError = t3 === undefined ? false : t3;
  const open = t4 === undefined ? true : t4;
  const hostActive = t5 === undefined ? true : t5;
  const t = useTranslations("onramp");
  const locale = useLocale();
  const onrampEnabled = useFeatureGate("onramp_enabled");
  const directPurchaseEnabled = useFeatureGate("direct_token_purchase_enabled");
  const selectedCurrency = useCurrencyStore(_temp);
  const rates = useExchangeRates();
  const {
    currency,
    decimals,
    fx
  } = useFundingCurrency(selectedCurrency, rates.fundingFx);
  const pageActive = useFundingPageActive();
  const t6 = onrampEnabled === true && directPurchaseEnabled === true;
  const t7 = hostActive && open && pageActive && !authenticationBusy;
  const t8 = directPurchase ? directPurchaseFiat.currency : currency;
  let t9;
  if ($[0] !== directPurchase || $[1] !== fx) {
    t9 = directPurchase ? {
      directPurchase
    } : {
      fundingFx: fx
    };
    $[0] = directPurchase;
    $[1] = fx;
    $[2] = t9;
  } else {
    t9 = $[2];
  }
  const t10 = rates.isLoading;
  const t11 = directPurchase ? directPurchaseFiat.decimals : decimals;
  let t12;
  if ($[3] !== t) {
    t12 = status => {
      showToast({
        id: TOAST_IDS.ONRAMP_PAYMENT_STATUS,
        message: t(`status.${status}`),
        variant: "info"
      });
    };
    $[3] = t;
    $[4] = t12;
  } else {
    t12 = $[4];
  }
  let t13;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = crypto.randomUUID.bind(crypto);
    $[5] = t13;
  } else {
    t13 = $[5];
  }
  let t14;
  if ($[6] !== authenticationRequested || $[7] !== client || $[8] !== locale || $[9] !== onSessionRefresh || $[10] !== provider || $[11] !== rates.isLoading || $[12] !== sessionProofId || $[13] !== t11 || $[14] !== t12 || $[15] !== t6 || $[16] !== t7 || $[17] !== t8 || $[18] !== t9) {
    t14 = {
      client,
      directPurchaseEnabled: t6,
      manifest: onrampManifest,
      sessionProofId,
      limitStorage: fundingLimitStorage,
      authenticationRequested,
      provider,
      surface: "web",
      active: t7,
      currency: t8,
      ...t9,
      fundingFxLoading: t10,
      locale,
      decimals: t11,
      deviceMethods: webFundingMethods,
      onPaymentStatus: t12,
      onConnectionCheck: _temp2,
      onSessionRefresh,
      createId: t13
    };
    $[6] = authenticationRequested;
    $[7] = client;
    $[8] = locale;
    $[9] = onSessionRefresh;
    $[10] = provider;
    $[11] = rates.isLoading;
    $[12] = sessionProofId;
    $[13] = t11;
    $[14] = t12;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const checkout = useFundingInlineCheckout(t14);
  const t15 = checkout.state === "methods" ? null : checkout.method;
  let t16;
  if ($[20] !== checkout.amountUnits || $[21] !== checkout.currency || $[22] !== checkout.decimals || $[23] !== locale || $[24] !== t15) {
    t16 = {
      method: t15,
      currency: checkout.currency,
      decimals: checkout.decimals,
      locale,
      amountUnits: checkout.amountUnits
    };
    $[20] = checkout.amountUnits;
    $[21] = checkout.currency;
    $[22] = checkout.decimals;
    $[23] = locale;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const amountDraft = useFundingAmountDraft(t16);
  const boundaryUnits = checkout.amountBoundary?.amountUnits;
  const boundaryKind = checkout.amountBoundary?.kind;
  const enteredUnits = checkout.amountUnits;
  let t17;
  if ($[26] !== checkout.currency || $[27] !== checkout.decimals || $[28] !== locale || $[29] !== t) {
    t17 = (units, kind) => {
      const amount = formatFiatUnits(units, checkout.decimals, checkout.currency, locale);
      showToast({
        id: TOAST_IDS.ONRAMP_AMOUNT_ERROR,
        message: kind === "minimum" ? t("amount_minimum", {
          amount
        }) : t("amount_maximum", {
          amount
        }),
        variant: "error"
      });
    };
    $[26] = checkout.currency;
    $[27] = checkout.decimals;
    $[28] = locale;
    $[29] = t;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const notifyAmountBoundary = useEffectEvent(t17);
  let t18;
  if ($[31] !== boundaryKind || $[32] !== boundaryUnits || $[33] !== checkout.state || $[34] !== enteredUnits || $[35] !== notifyAmountBoundary) {
    t18 = () => {
      if (!boundaryUnits || !boundaryKind || !enteredUnits || checkout.state !== "amount") {
        return;
      }
      const timer = setTimeout(() => {
        notifyAmountBoundary(boundaryUnits, boundaryKind);
      }, 800);
      return () => clearTimeout(timer);
    };
    $[31] = boundaryKind;
    $[32] = boundaryUnits;
    $[33] = checkout.state;
    $[34] = enteredUnits;
    $[35] = notifyAmountBoundary;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== boundaryKind || $[38] !== boundaryUnits || $[39] !== checkout.state || $[40] !== enteredUnits) {
    t19 = [boundaryUnits, boundaryKind, enteredUnits, checkout.state];
    $[37] = boundaryKind;
    $[38] = boundaryUnits;
    $[39] = checkout.state;
    $[40] = enteredUnits;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  useEffect(t18, t19);
  const t20 = directPurchase ? "direct_purchase_title" : "deposit_title";
  let t21;
  if ($[42] !== t || $[43] !== t20) {
    t21 = t(t20);
    $[42] = t;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== checkout.customerVerification || $[46] !== checkout.state || $[47] !== hostActive) {
    t22 = hostActive && checkoutOwnsChrome(checkout.state, checkout.customerVerification);
    $[45] = checkout.customerVerification;
    $[46] = checkout.state;
    $[47] = hostActive;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  useFundingStage(t21, checkout.state === "methods", checkout.hasBack ? checkout.back : undefined, t22, checkout.state === "methods" && !checkout.loading ? checkout.openProfile : undefined);
  let t23;
  if ($[49] !== checkout.error || $[50] !== checkout.preview.error || $[51] !== checkout.preview.providerFailure?.code || $[52] !== checkout.providerFailure?.code || $[53] !== checkout.state) {
    t23 = () => {
      const code = checkout.error ?? checkout.preview.error;
      if (code) {
        logger.warn("onramp.checkout_failed", {
          phase: checkout.state,
          code,
          providerCode: checkout.providerFailure?.code ?? checkout.preview.providerFailure?.code
        });
      }
    };
    $[49] = checkout.error;
    $[50] = checkout.preview.error;
    $[51] = checkout.preview.providerFailure?.code;
    $[52] = checkout.providerFailure?.code;
    $[53] = checkout.state;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== checkout.error || $[56] !== checkout.preview.error || $[57] !== checkout.preview.providerFailure || $[58] !== checkout.providerFailure || $[59] !== checkout.state) {
    t24 = [checkout.error, checkout.preview.error, checkout.state, checkout.providerFailure, checkout.preview.providerFailure];
    $[55] = checkout.error;
    $[56] = checkout.preview.error;
    $[57] = checkout.preview.providerFailure;
    $[58] = checkout.providerFailure;
    $[59] = checkout.state;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  useEffect(t23, t24);
  let t25;
  if ($[61] !== directPurchase) {
    t25 = directPurchase ? {
      directPurchase
    } : {};
    $[61] = directPurchase;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== realtime) {
    t26 = realtime ? {
      realtime
    } : {};
    $[63] = realtime;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== onAcceptAuthenticationTerms) {
    t27 = onAcceptAuthenticationTerms ? {
      onAcceptAuthenticationTerms
    } : {};
    $[65] = onAcceptAuthenticationTerms;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] !== amountDraft || $[68] !== authenticationBusy || $[69] !== authenticationError || $[70] !== checkout || $[71] !== client || $[72] !== onClose || $[73] !== onExchange || $[74] !== onSignIn || $[75] !== provider || $[76] !== sessionProofId || $[77] !== t25 || $[78] !== t26 || $[79] !== t27) {
    t28 = <FundingCheckoutView {...t25} checkout={checkout} amountDraft={amountDraft} client={client} {...t26} sessionProofId={sessionProofId} authenticationBusy={authenticationBusy} authenticationError={authenticationError} {...t27} provider={provider} onSessionRefresh={checkout.retry} onSignIn={onSignIn} onExchange={onExchange} onClose={onClose} currency={checkout.currency} decimals={checkout.decimals} />;
    $[67] = amountDraft;
    $[68] = authenticationBusy;
    $[69] = authenticationError;
    $[70] = checkout;
    $[71] = client;
    $[72] = onClose;
    $[73] = onExchange;
    $[74] = onSignIn;
    $[75] = provider;
    $[76] = sessionProofId;
    $[77] = t25;
    $[78] = t26;
    $[79] = t27;
    $[80] = t28;
  } else {
    t28 = $[80];
  }
  const content = t28;
  let t29;
  if ($[81] !== content || $[82] !== render) {
    t29 = render ? render(content) : content;
    $[81] = content;
    $[82] = render;
    $[83] = t29;
  } else {
    t29 = $[83];
  }
  return t29;
}
function _temp2(status_0) {
  if (status_0 === "connectionRequired") {
    logger.warn("onramp.reauthentication_required", {
      source: "moonpay_connection_check"
    });
  }
}
function _temp(state) {
  return state.currency;
}